<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :embedded="embedded"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <div class="row no-gutters h-100">
      <div class="col-2 p-2">
        <AnalyticsDashboardValueRow>
          <div class="col">
            <ReportFilterPopover
              headerTitle="Toughening Station Task Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :appendToBody="true"
              alignment="left"
              placement="bottom-end"
              @apply="applyStationFilter"
              @reset="resetStationFilter">
              <template v-slot:popover>
                <ReportDateRangeSelect 
                  :startDate="editingStationFilter.startDate"
                  :endDate="editingStationFilter.endDate"
                  @updateStartDate="editingStationFilter.startDate = $event"
                  @updateEndDate="editingStationFilter.endDate = $event"
                />
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedStationFilter.startDate | formatDate}} - {{appliedStationFilter.endDate | formatDate}}
                </div>                
              </template>
            </ReportFilterPopover>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Average Yield" highlight>
              {{formatPercent(tougheningYieldSummaryData.averageBedYieldPercent)}}
              <template v-slot:info>
                Based on {{tougheningYieldSummaryData.totalPanelCount}} panel{{displayCountPlural(tougheningYieldSummaryData.totalPanelCount)}} on {{tougheningYieldSummaryData.totalBedLogCount}} bed{{displayCountPlural(tougheningYieldSummaryData.totalBedLogCount)}}
              </template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
      </div>
      <div class="col-10">
        <ChartBar
          title="Daily Toughening Bed Yields"
          :series="stationDailyData.series"
          :xAxis="stationDailyData.xAxis"
          :yAxis="stationDailyData.yAxis"
          :legend="stationDailyData.legend"
          :options="stationDailyData.options"
          :popupEnabled="true"
          :exportEnabled="true"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import Utils from '@/utils/utils'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ChartBar from '@/components/charts/chartBar'

import { ProductionStationTypes } from '@/store/types'

export default {
  name: 'ProductionToughYieldChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      tougheningYieldSummaryData: {
        averageBedYieldPercent: 0,
        totalBedLogCount: 0,
        totalPanelCount: 0,
        totalPanelArea: 0
      },
      stationDailyData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultStationFilter: {    
        startDate: moment().local().subtract(90, 'days').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString()
      },
      editingStationFilter: {},
      appliedStationFilter: {},

      tougheningStationLookup: []
    }
  },
  computed: {
  },
  mounted() {
    this.editingStationFilter = _.clone(this.defaultStationFilter)
    this.appliedStationFilter = _.clone(this.defaultStationFilter)

    // Note: Only load chart data when we have a production station set
    this.requestData()

    this.subscribeEndpoint('/production/sheetschedule', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/production/sheetschedule')
  },
  watch: {
  },
  methods: {
    applyStationFilter() {
      this.appliedStationFilter = _.clone(this.editingStationFilter)

      this.requestChartData()
    },
    resetStationFilter() {
      this.editingStationFilter = _.clone(this.defaultStationFilter)
      this.appliedStationFilter = _.clone(this.defaultStationFilter)

      this.requestChartData()
    },
    requestData() {
      // Load the production station
      this.$apiService.productionService.getProductionStationList()
      .then(response => {
        this.tougheningStationLookup = response.data
        .filter(productionStation => productionStation.isActive && productionStation.type == ProductionStationTypes.furnace)
        .map(productionStation => {
          return {
            value: productionStation.id,
            name: productionStation.name
          }
        })

        // Apply the 1st station to the default filter
        if(this.tougheningStationLookup.length > 0) {
          this.defaultStationFilter.productionStationId = this.tougheningStationLookup[0].value
          this.resetStationFilter()
        }
      })
      .catch(err => {
        this.displayNetworkError(err, 'Could not load data')
      })
    },
    requestChartData() {
      var widgetComp = this

      var stationFilter = {
        type: 'daily',
        startDate: this.appliedStationFilter.startDate,
        endDate: this.appliedStationFilter.endDate
      }

      this.$apiService.analyticsService.getTougheningYieldChartData(stationFilter)
      .then(response => {
        let chartData = response.data

        // Group by stations
        var seriesList = this.tougheningStationLookup.map(stationInfo => {
          var stationData = chartData.filter(chartEntry => chartEntry.productionStationId == stationInfo.value)

          return {
            name: stationInfo.name,
            type: 'bar',
            data: stationData.map(chartEntry => chartEntry.averageBedYield),
            tooltip: {
              valueFormatter(value) {
                return widgetComp.formatPercent(value);
              }
            }
          }
        })

        this.stationDailyData.legend = {
          data: this.tougheningStationLookup.map(stationInfo => stationInfo.name)
        }
        this.stationDailyData.xAxis = {
          type: 'category',
          exportName: 'Date',
          exportData: _.uniq(chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM YYYY'))),
          data: _.uniq(chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM')))
        }
        this.stationDailyData.yAxis = [
          {
            type: 'value',
            axisLabel: {
              formatter: (value) => {
                return this.formatPercent(value);
              }
            }
          }
        ]
        this.stationDailyData.series = seriesList

        // Summary Info
        this.tougheningYieldSummaryData.averageBedYieldPercent = 0
        if(chartData.length > 0) {
          this.tougheningYieldSummaryData.averageBedYieldPercent = chartData.reduce((curValue, chartEntry) => curValue + chartEntry.averageBedYield, 0) / chartData.length
        }
        this.tougheningYieldSummaryData.totalBedLogCount = chartData.reduce((curValue, chartEntry) => curValue + chartEntry.totalBedLogCount, 0)
        this.tougheningYieldSummaryData.totalPanelCount = chartData.reduce((curValue, chartEntry) => curValue + chartEntry.totalPanelCount, 0)
        this.tougheningYieldSummaryData.totalPanelArea = chartData.reduce((curValue, chartEntry) => curValue + chartEntry.totalPanelArea, 0)
      })
    },
  }
}
</script>